<template>
  <article class="account-dashboard">
    <div class="container">
      <header class="dashboard-header">
        <router-link :to="hasMultipleAccounts ? `/app/clients/${$route.params.id}/overview` : `/app/clients/${$route.params.id}`" class="back">
          <svgicon name="chevron-down" class="chevron" :alt="$t('Back')" />
          <span class="link-label">{{ hasMultipleAccounts ? 'Overview' : (firstName || 'Back') }}</span>
        </router-link>
        <h4 class="title">{{ accountName || accountTypeName }}</h4>
      </header>
      <section v-if="loaded && !hasData" class="no-data">
        <img :src="`~@/assets/images/${brand}/dashboard-empty@2x.png`" class="empty-graph">
        <h3 v-t>No account activity, yet.</h3>
        <p v-t>There's no funds to show. We may be waiting for transfers to arrive.</p>
      </section>
      <account-graph
        v-if="loaded && hasData"
        class="graph"
        :timeframe="timeframe"
        :balance="balance"
        :cashflows="cashflows"
        :ror="ror"
        :gains="gains"
        :dates="dates"
        :balances="balances"
        :contributions="contributions"
        @time-frame-changed="changeTimeFrame"
      />
      <section v-if="hasData" class="card">
        <header class="portfolio-header">
          <h5 v-t class="portfolio-title">
            <span v-t>Portfolio:</span>
            &nbsp; {{ portfolioName || '' }}
          </h5>
          <small class="portfolio-date">
            <span v-t>
              as of
            </span>
            &nbsp; {{ lastUpdateDate | formatDate }}
          </small>
        </header>
        <tabs :tabs="tabs" class="card-content">
          <template #asset-classes>
            <ul>
              <li v-for="(assetClass, index) in assetClasses" :key="assetClass.id">
                <div>{{ assetClass.name ? assetClass.name.substring(0, 30) : '' }}</div>
                <div><span class="amount-value">{{ assetClass.amount | formatCurrency }}</span></div>
                <div class="allocation-container"><span class="allocation-value">{{ assetClass.allocation | formatPercentageRounded }}</span></div>
                <div class="bar-container">
                  <span
                    class="bar" :class="getAllocationStyle(index)"
                    :style="{ 'width': getBarWidth(assetClasses[0].allocation, assetClass.allocation) }"
                  />
                </div>
              </li>
            </ul>
          </template>
          <template #holdings>
            <ul class="tab-table">
              <li v-for="(holding, index) in holdings" :key="holding.symbol">
                <div>{{ `${holding.symbol} ${getFirstWord(holding.description)}` }}</div>
                <div><span class="amount-value">{{ holding.amount | formatCurrency }}</span></div>
                <div class="allocation-container"><span class="allocation-value">{{ holding.allocation | formatPercentageRounded }}</span></div>
                <div class="bar-container">
                  <span class="bar" :class="getAllocationStyle(index)" :style="{ 'width': getBarWidth(holdings[0].allocation, holding.allocation) }" />
                </div>
              </li>
            </ul>
          </template>
        </tabs>
      </section>
    </div>
  </article>
</template>

<script>
import moment from 'moment';
import filters, { formatDateShortMonth } from '@/lib/utils/filters';
import getBrandConfig from '@/lib/utils/get_brand_config';
import { tabs } from '@/components/layout';
import { accountGraph } from '@/components/advisor';
import getInvestmentAccount from '@/store/data-providers/get_investment_account';
import getFirstWord from '@/lib/utils/get_first_word';
import '@/lib/icons/chevron-down';
import errorStore from '@/store/error';

const _ = {
  getInvestmentAccount,
  formatDateShortMonth,
  getFirstWord,
  brandConfig: getBrandConfig(),
  errorStore,
};

export default {
  _,

  route: {
    path: '/app/clients/:id/:accountId',
    priority: -1,
  },

  head() {
    return {
      title: `${_.brandConfig.fullName}`,
    };
  },

  components: {
    tabs,
    accountGraph,
  },

  filters,

  data() {
    return {
      brand: _.brandConfig.shortName,
      loaded: false,
      firstName: null,
      portfolioName: null,
      lastUpdateDate: null,
      hasMultipleAccounts: null,
      accountName: null,
      accountTypeName: null,
      hasData: null,
      timeframe: 'all',
      balance: null,
      cashflows: null,
      ror: null,
      gains: null,
      dates: null,
      balances: null,
      contributions: null,
      assetClasses: null,
      holdings: null,
      allocationStyles: [
        'colour1',
        'colour2',
        'colour3',
        'colour4',
        'colour5',
        'colour6',
        'colour7',
        'colour8',
        'colour9',
        'colour10',
      ],
      tabs: [
        {
          id: 'asset-classes',
          name: 'Asset Classes',
        },
        {
          id: 'holdings',
          name: 'Holdings',
        },
      ],
    };
  },

  async created() {
    await this.updateData();
  },

  methods: {
    getFirstWord,
    getAllocationStyle(index) {
      if (index >= this.allocationStyles.length) {
        return this.allocationStyles[this.allocationStyles.length - 1];
      }
      return this.allocationStyles[index];
    },
    getBarWidth(highestAllocation, allocation) {
      return `${(85 * allocation) / highestAllocation}%`;
    },
    timeframeToDate() {
      let startDate = null;

      if (this.timeframe === '1M') {
        startDate = moment().add(-1, 'month');
      } else if (this.timeframe === '6M') {
        startDate = moment().add(-6, 'month');
      } else if (this.timeframe === '1Y') {
        startDate = moment().add(-1, 'year');
      } else if (this.timeframe === 'YTD') {
        startDate = moment().set({ date: 1, month: 0 });
      }

      return startDate ? startDate.format('YYYY-MM-DD') : null;
    },
    async updateData() {
      const startDate = this.timeframeToDate();
      try {
        const {
          hasData,
          accountInfo,
          accountSumUp,
          dataPoints,
          assetClasses,
          holdings,
        } = await _.getInvestmentAccount(this.$route.params.accountId, startDate);

        this.setAccountInfo(accountInfo);

        this.hasData = hasData;
        if (hasData) {
          this.setAccountSumUp(accountSumUp);
          this.setGraphDataPoints(dataPoints);
          this.assetClasses = assetClasses;
          this.holdings = holdings;
        }
        // We need a loader to display something while it is loading
        this.loaded = true;
      } catch (exception) {
        _.errorStore.addError();
      }
    },
    setAccountInfo(accountInfo) {
      this.firstName = accountInfo.firstName;
      this.portfolioName = accountInfo.portfolioName;
      this.lastUpdateDate = accountInfo.lastUpdateDate;
      this.hasMultipleAccounts = accountInfo.hasMultipleAccounts;
      this.accountName = accountInfo.accountName;
      this.accountTypeName = accountInfo.accountTypeName;
    },
    setAccountSumUp(accountSumUp) {
      this.balance = parseFloat(accountSumUp.endBalance);
      this.cashflows = accountSumUp.cashFlows;
      this.ror = accountSumUp.ror;
      this.gains = accountSumUp.return;
    },
    setGraphDataPoints(dataPoints) {
      this.dates = dataPoints.dates.map(date => _.formatDateShortMonth(date));
      this.balances = dataPoints.balances;
      this.contributions = dataPoints.contributions;
    },
    changeTimeFrame(newTimeframe) {
      this.timeframe = newTimeframe;
      this.updateData();
    },
  },
};
</script>

<style lang="scss" scoped>
  .account-dashboard {
    padding: 0 0.75rem;
    background-color: $pearl-50;
    overflow-y: auto;

    .container {
      max-width: 80rem;
      width: 100%;
      margin: 0 auto;
      padding-bottom: 1rem;
    }
  }

  .dashboard-header {
    height: 3.5rem;
    line-height: 3.5rem;
    text-align: center;
    border-bottom: 1px solid $pearl-300;

    .back {
      position: absolute;
      height: 100%;
      left: 0;
      display: flex;
      align-items: center;
      text-decoration: none;

      .link-label {
        display: none;

        @media #{$screen-width-medium} {
          display: inline;
        }
      }
    }

    .chevron {
      transform: rotate(90deg);
      margin-right: 0.3rem;
    }

    .title {
      display: inline-block;
      line-height: 1;
      text-align: center;
      margin: 0;
    }
  }

  .no-data {
    max-width: 20rem;
    margin: 0 auto;
    text-align: center;

    .empty-graph {
      max-width: 15rem;
      margin-top: 5rem;
    }
  }

  .graph {
    margin-top: 2.5rem;
  }

  .portfolio-title {
    margin: 0;

    @media #{$screen-width-medium} {
      display: inline-block;
      margin-right: 0.4rem;
    }
  }

  .portfolio-date {
    color: $pearl-700;
  }

  ul {
    li {
      border-bottom: 1px solid $neutral-300;
      &:last-child {
        border-bottom: none;
      }
      @media #{$screen-width-medium} {
        display: grid;
        grid-template-columns: 30% 20% 3rem 40%;
        & > div { padding: 0.5rem 0; }
      }
    }
  }

  .amount-value,
  .allocation-value {
    text-align: right;
  }

  .bar {
    display: inline-block;
    height: 0.375rem;
    border-radius: 0 0.375rem 0.375rem 0;
  }

  .colour1 { background-color: $black; }

  .colour2 { background-color: $primary-700; }

  .colour3 { background-color: $primary-500; }

  .colour4 { background-color: #00a593; }

  .colour5 { background-color: $primary-300; }

  .colour6 { background-color: #3bc6ae; }

  .colour7 { background-color: $primary-100; }

  .colour8 { background-color: #86d6c0; }

  .colour9 { background-color: #a4e0d0; }

  .colour10 { background-color: #c2ebe0; }
</style>
